import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Layout } from "@/components/layout";
import { StatCard } from "@/components/ui/stat-card";
import { IncomeExpensesChart } from "@/components/dashboard/income-expenses-chart";
import { ExpenseBreakdownChart } from "@/components/dashboard/expense-breakdown-chart";
import { FinancialInsightsCard } from "@/components/dashboard/financial-insights";
import { TaskList } from "@/components/ui/task-list";
import { AIAssistantCompact } from "@/components/dashboard/ai-assistant-compact";
import { BankAccountsSection } from "@/components/dashboard/bank-accounts-section";
import { HealthScore } from "@/components/ui/health-score";
import { TransactionTable } from "@/components/ui/transaction-table";
import { WelcomeDashboard } from "@/components/dashboard/welcome-dashboard";
import { Button } from "@/components/ui/button";
import { useTransactions } from "@/hooks/use-transactions";
import { useTasks } from "@/hooks/use-tasks";
import { useBusinesses } from "@/hooks/use-businesses";
import { useBusinessContext } from "@/providers/business-provider";
import { useLoading } from "@/providers/loading-provider";
import { useAuth } from "@/providers/auth-provider";
import { ActivationNudge } from "@/components/dashboard/activation-nudge";
import { TrialStatusBanner } from "@/components/ui/trial-status-banner";
import { 
  ReceiptText,
  Download, 
  Plus,
  BadgeDollarSign,
  Receipt,
  Wallet,
  ArrowUpFromLine
} from "lucide-react";
import { format, subMonths } from "date-fns";
import { ImportTransactions } from "@/components/transactions/import-transactions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TrialStatus } from "@/components/subscription/trial-status";
import { ClientSummaryCards } from "@/components/dashboard/client-summary-cards";

export default function DashboardPage() {
  // Get business ID from URL if present
  const urlParams = new URLSearchParams(window.location.search);
  const urlBusinessId = urlParams.get('business');
  
  // Use the business from context provider only
  const { businesses, activeBusiness, isLoading: isLoadingBusinesses } = useBusinessContext();
  const { navigateTo } = useLoading();
  const { user } = useAuth();
  // Per-business subscription comes from the businesses list (loaded by the
  // context provider via /api/businesses). Avoids a stale shared cache that
  // used to leak the previously-viewed business's plan into a new business.
  const currentSubscription: any = (activeBusiness as any)?.subscription ?? null;
  const [showImportModal, setShowImportModal] = useState(false);
  
  // Used for debugging
  useEffect(() => {
    console.log("Dashboard page - URL business ID:", urlBusinessId);
    console.log("Dashboard page - Active business:", activeBusiness?.name, activeBusiness?.id);
  }, [urlBusinessId, activeBusiness]);
  
  // Get current month start and end
  const today = new Date();
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  
  // Get last month for comparison
  const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
  
  // Format dates for API
  const startDateStr = format(startOfMonth, "yyyy-MM-dd");
  const endDateStr = format(endOfMonth, "yyyy-MM-dd");
  const lastMonthStartStr = format(startOfLastMonth, "yyyy-MM-dd");
  const lastMonthEndStr = format(endOfLastMonth, "yyyy-MM-dd");
  
  // Get transactions for the active business specifically - force business ID to be a number
  const activeBusinessId = activeBusiness ? activeBusiness.id : undefined;
  console.log("Dashboard using business ID:", activeBusinessId, "Business name:", activeBusiness?.name);
  
  const { 
    transactions: currentTransactions, 
    isLoading: isLoadingCurrentTransactions 
  } = useTransactions(activeBusinessId, {
    startDate: startDateStr,
    endDate: endDateStr,
    limit: 5
  });
  
  // Get previous month transactions for comparison
  const { 
    transactions: lastMonthTransactions, 
    isLoading: isLoadingLastMonthTransactions 
  } = useTransactions(activeBusinessId, {
    startDate: lastMonthStartStr,
    endDate: lastMonthEndStr
  });
  
  // Get tasks for the specific business
  const { 
    tasks, 
    toggleTask, 
    isPendingToggle 
  } = useTasks({ status: "pending" });
  
  // Fetch real bank balance from Plaid accounts
  const { data: plaidAccounts } = useQuery<any[]>({
    queryKey: ["/api/plaid-accounts-balance", activeBusinessId],
    queryFn: async () => {
      try {
        const r = await fetch(`/api/businesses/${activeBusinessId}/plaid/linked-accounts`, { credentials: "include" });
        if (!r.ok) return [];
        const data = await r.json();
        return Array.isArray(data) ? data.filter((a: any) => a.isSelected !== false) : [];
      } catch { return []; }
    },
    enabled: !!activeBusinessId,
    staleTime: 60000,
  });
  const bankBalance = (plaidAccounts || []).reduce((sum: number, a: any) => sum + (parseFloat(a.balanceCurrent || a.balance_current || '0') || 0), 0);

  // Check if business has any transactions first
  const hasTransactions = currentTransactions && currentTransactions.length > 0;
  
  // Only show real transaction data, not synthetic data
  // Calculate totals from current business transactions only
  const currentMonthRevenue = currentTransactions
    ?.filter(t => t.type === "income")
    .reduce((sum, t) => sum + parseFloat(t.amount.toString()), 0) || 0;
  
  const currentMonthExpenses = currentTransactions
    ?.filter(t => t.type === "expense")
    .reduce((sum, t) => sum + parseFloat(t.amount.toString()), 0) || 0;
  
  const lastMonthRevenue = lastMonthTransactions
    ?.filter(t => t.type === "income")
    .reduce((sum, t) => sum + parseFloat(t.amount.toString()), 0) || 0;
  
  const lastMonthExpenses = lastMonthTransactions
    ?.filter(t => t.type === "expense")
    .reduce((sum, t) => sum + parseFloat(t.amount.toString()), 0) || 0;
  
  // Calculate profits and cash balance from real data only
  const currentMonthProfit = currentMonthRevenue - currentMonthExpenses;
  const lastMonthProfit = lastMonthRevenue - lastMonthExpenses;
  
  // Use real bank balance from Plaid if available, else fall back to calculated
  const cashBalance = bankBalance > 0 ? bankBalance : (hasTransactions ? (currentMonthRevenue - currentMonthExpenses) : 0);
  const prevCashBalance = hasTransactions ? (lastMonthRevenue - lastMonthExpenses) : 0;
  
  // Calculate trends
  const revenueTrend = lastMonthRevenue ? ((currentMonthRevenue - lastMonthRevenue) / lastMonthRevenue) * 100 : 0;
  const expensesTrend = lastMonthExpenses ? ((currentMonthExpenses - lastMonthExpenses) / lastMonthExpenses) * 100 : 0;
  const profitTrend = lastMonthProfit ? ((currentMonthProfit - lastMonthProfit) / Math.abs(lastMonthProfit)) * 100 : 0;
  const cashTrend = prevCashBalance ? ((cashBalance - prevCashBalance) / prevCashBalance) * 100 : 0;
  
  // Health score metrics
  const healthMetrics = [
    {
      label: "Cash Flow",
      value: cashTrend > 0 ? "Strong" : "Moderate",
      status: cashTrend > 0 ? "Positive" : "Neutral",
      trend: cashTrend > 0 ? "up" : "down"
    },
    {
      label: "Profit Margin",
      value: currentMonthRevenue ? `${((currentMonthProfit / currentMonthRevenue) * 100).toFixed(1)}%` : "0%",
      status: currentMonthProfit > lastMonthProfit ? "Above Target" : "Below Target"
    },
    {
      label: "Expense Management",
      value: expensesTrend < 0 ? "Good" : "Moderate",
      status: "Trending Up"
    }
  ];
  
  // Health score value based on metrics
  const healthScore = Math.min(100, Math.max(0, Math.round(75 + profitTrend/2 - expensesTrend/2)));

  // Guard: show welcome dashboard when no business is selected or none exist
  const { needsBusinessSelection } = useBusinessContext();
  if (needsBusinessSelection || !activeBusiness) {
    return <Layout><WelcomeDashboard /></Layout>;
  }

  const fmtCurrency = (v: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
  const fmtTrend = (v: number) => (v > 0 ? "+" : "") + v.toFixed(1) + "%";
  const monthLabel = format(today, "MMMM yyyy");

  return (
    <Layout>
      <div className="space-y-6">
        {/* ── Header ─────────────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">{activeBusiness?.name || "Unnamed Business"}</h1>
            <p className="text-xs text-gray-400 mt-0.5">
              ID {(activeBusiness as any)?.publicId || (activeBusiness as any)?.public_id || activeBusiness?.id}
              {(() => {
                const sub: any = currentSubscription;
                let label: string;
                if (!sub) label = "No Plan";
                else if (sub.isTrial || sub.is_trial) label = "Free Trial";
                else label = sub.planName || sub.plan_name || "No Plan";
                return (
                  <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 font-medium text-[10px]">
                    {label}
                  </span>
                );
              })()}
            </p>
          </div>
          <p className="text-xs text-gray-400">{monthLabel}</p>
        </div>

        {/* ── Activation Nudge (no bank) ─────────────────────────── */}
        <ActivationNudge />

        {/* ── KPI Row ────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: "Revenue", value: currentMonthRevenue, trend: revenueTrend, color: "text-blue-600" },
            { label: "Expenses", value: currentMonthExpenses, trend: expensesTrend, color: "text-orange-600" },
            { label: "Net Profit", value: currentMonthProfit, trend: profitTrend, color: currentMonthProfit >= 0 ? "text-green-600" : "text-red-600" },
            { label: "Cash Balance", value: cashBalance, trend: cashTrend, color: "text-gray-900" },
          ].map((kpi) => (
            <Card key={kpi.label} className="shadow-sm border-gray-100">
              <CardContent className="p-4 sm:p-5">
                <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">{kpi.label}</p>
                <p className={`text-xl sm:text-2xl font-bold mt-1 ${kpi.color}`}>{fmtCurrency(kpi.value)}</p>
                {kpi.trend !== 0 && (
                  <p className={`text-[11px] mt-1 font-medium ${kpi.trend > 0 ? "text-green-600" : "text-red-500"}`}>
                    {fmtTrend(kpi.trend)} vs last month
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* ── Charts + Insights (2-column) ───────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left: Income vs Expenses bar chart */}
          <div className="lg:col-span-3">
            <IncomeExpensesChart isLoading={isLoadingCurrentTransactions || isLoadingLastMonthTransactions} />
          </div>
          {/* Right: Expense breakdown + AI insights */}
          <div className="lg:col-span-2 space-y-6">
            <ExpenseBreakdownChart isLoading={isLoadingCurrentTransactions} />
            <FinancialInsightsCard />
          </div>
        </div>

        {/* ── Bank Accounts ──────────────────────────────────────── */}
        <BankAccountsSection />

        {/* ── Recent Transactions ─────────────────────────────────── */}
        <Card className="shadow-sm border-gray-100">
          <CardHeader className="px-5 py-3 flex justify-between items-center border-b">
            <CardTitle className="text-sm font-semibold">Recent Transactions</CardTitle>
            <Button variant="link" className="p-0 h-auto text-xs" onClick={() => navigateTo('/transactions')}>
              View all
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            <TransactionTable
              transactions={currentTransactions || []}
              isLoading={isLoadingCurrentTransactions}
            />
          </CardContent>
        </Card>
      </div>

      <ImportTransactions open={showImportModal} onOpenChange={setShowImportModal} />
    </Layout>
  );
}
